/**
 * Trade Processor — Single Source of Truth
 *
 * Pure utility functions for processing NinjaTrader executions into trades.
 * No React, no IPC, no localStorage — works in any context.
 *
 * Used by:
 *   - useJournifiSync.js (real-time auto-sync hook)
 *   - journifiSyncService.js (manual Preview/Import)
 *
 * Architecture (real-time foundation):
 *   1. groupExecutionsIntoTrades() — FIFO position tracking, returns intermediate trades
 *   2. buildJournalTrade() — converts intermediate trade to final journal format
 *   3. processExecutionsToTrades() — convenience wrapper combining both
 *
 * The intermediate trade format is the "live trade state" — designed so that
 * future real-time in-progress journaling can call buildJournalTrade() repeatedly
 * as exits/trims arrive to update an open trade.
 *
 * Author: AlphaWolfTrader
 */

import { futuresSpecs, safeTicks, snapToTick, getSpec, extractContractMonth } from '../config/futuresSpecs';

// Trading-day boundary helpers — single source of truth lives in timeService.
// Imported for in-file use AND re-exported so existing callers (useJournifiSync,
// journifiSyncService, etc.) keep working without an import-path migration.
import { getTradingDay, getCurrentTradingDay, formatEtTime } from './timeService.js';
export { getTradingDay, getCurrentTradingDay };

// ─────────────────────────────────────────────
// Pure Utilities
// ─────────────────────────────────────────────

// Dedup helpers (filterAlreadyJournaledExecutions, dedupExecutionsByExecutionId)
// live in `./executionDedup.js` so they're testable from node ESM without
// pulling in this file's bundler-only imports (`../config/futuresSpecs`,
// etc., which Node can't resolve extensionless). Re-exported here so callers
// already importing from `tradeProcessor` don't need to change paths.
export { filterAlreadyJournaledExecutions, dedupExecutionsByExecutionId } from './executionDedup.js';

/**
 * Parse ATM template name to extract stop loss, contracts, risk.
 * Example: "1) 21 - 12pSL - 5C - 2-3R - 4AT - 221   ($120 Risk - 1 Runner)"
 *
 * @param {string} templateName - Raw ATM template name from NinjaTrader
 * @returns {{ name: string, stopLossPoints: number|null, contractCount: number|null, riskAmount: number|null }}
 */
export const parseAtmTemplateName = (templateName) => {
  if (!templateName) return { name: '', stopLossPoints: null, contractCount: null, riskAmount: null };

  // Remove leading number prefix like "0) " or "1) 21 - "
  let name = templateName.replace(/^\d+\)\s*(\d+\s*-\s*)?/, '').trim();

  // Extract stop loss points from pattern like "12pSL"
  const slMatch = name.match(/(\d+)pSL/i);
  const stopLossPoints = slMatch ? parseInt(slMatch[1]) : null;

  // Extract contract count from pattern like "5C"
  const contractMatch = name.match(/(\d+)C(?:\s|$|-)/i);
  const contractCount = contractMatch ? parseInt(contractMatch[1]) : null;

  // Extract risk amount from patterns like "($120 Risk"
  const riskMatch = name.match(/\$(\d+)\s*Risk/i);
  const riskAmount = riskMatch ? parseInt(riskMatch[1]) : null;

  // Clean up the name — remove the risk info in parentheses
  name = name.replace(/\s*\([^)]*\$[^)]*\)\s*$/, '').trim();

  return { name, stopLossPoints, contractCount, riskAmount };
};

/**
 * Format a timestamp to HH:MM string (local time).
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} - "HH:MM" or ""
 */
export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

// NT futures contract month code letters — 12 letters mapping to months:
// F=Jan, G=Feb, H=Mar, J=Apr, K=May, M=Jun, N=Jul, Q=Aug, U=Sep, V=Oct, X=Nov, Z=Dec
// Index futures (MNQ, MES, NQ, ES) only trade quarterly (H,M,U,Z), but
// monthly instruments (CL, NG, GC, KC, etc.) use all 12. The original
// strip regex only covered HMUZ — meaning a CL trade with InstrumentName
// "CLF6" (Jan crude) would have been stored as symbol "CLF6" instead of
// "CL", breaking every futuresSpecs lookup on that trade. Fixed in
// v7.5.2 alongside the broader contract-differentiation work.
const NT_MONTH_CODE_CHARS = 'FGHJKMNQUVXZ';
const NT_MONTH_CODE_REGEX = new RegExp(`[${NT_MONTH_CODE_CHARS}]\\d{1,2}$`);
const NT_MONTH_CODE_CAPTURE_REGEX = new RegExp(`([${NT_MONTH_CODE_CHARS}]\\d{1,2})$`);

/**
 * Extract clean symbol from execution data.
 * Strips contract month/year suffix (e.g., "MNQH26" -> "MNQ", "CLF6" -> "CL").
 *
 * Convention: trade.symbol is ALWAYS the root ticker (no contract month).
 * Contract month info is captured separately via getContractMonth() so
 * per-contract analytics can opt in without breaking the lookup contract
 * that 15+ downstream sites depend on.
 *
 * @param {Object} exec - Execution object
 * @returns {string} - Clean root symbol
 */
export const getSymbol = (exec) => {
  return exec.InstrumentType || (exec.InstrumentName || '').replace(NT_MONTH_CODE_REGEX, '');
};

/**
 * Extract contract month from execution data.
 *
 * Returns the NT short-code form (e.g., "H26", "M6") when present in
 * `InstrumentName`. Returns '' when the source provides only a root.
 * Paired with getSymbol() — both are pure functions of one execution.
 *
 *   InstrumentName: "MNQH26"   → "H26"
 *   InstrumentName: "MNQM6"    → "M6"
 *   InstrumentName: "CLF6"     → "F6"      (January crude — non-quarterly)
 *   InstrumentName: "MNQ"      → ""
 *   InstrumentName: undefined  → ""
 *
 * Why we capture this: per-contract performance analytics (e.g., "your
 * MNQ 03-26 contract performed differently than MNQ 06-26") need the
 * contract identity preserved. Storing the root in `symbol` keeps all
 * existing lookups working; storing the month in a parallel field
 * makes contract-aware analytics possible without a data migration.
 *
 * @param {Object} exec - Execution object
 * @returns {string} - Contract month code or '' if not present
 */
export const getContractMonth = (exec) => {
  const name = exec.InstrumentName || '';
  const m = name.match(NT_MONTH_CODE_CAPTURE_REGEX);
  return m ? m[1] : '';
};

/**
 * Determine if an action is a buy.
 * Supports both field name formats for backwards compatibility.
 *
 * @param {Object} exec - Execution object
 * @returns {boolean}
 */
export const isBuyAction = (exec) => {
  const action = exec.OrderAction || exec.Action || '';
  return action === 'Buy' || action === 'BuyToCover';
};

/**
 * Determine if an action is a sell.
 * @param {Object} exec - Execution object
 * @returns {boolean}
 */
export const isSellAction = (exec) => {
  const action = exec.OrderAction || exec.Action || '';
  return action === 'Sell' || action === 'SellShort';
};

// ─────────────────────────────────────────────
// Core: FIFO Position Tracking & Trade Grouping
// ─────────────────────────────────────────────

/**
 * Group executions into trades using FIFO position tracking.
 *
 * Groups by Account + Symbol, then tracks position changes to detect
 * individual trades. Supports position flips, scale-ins, multi-exit trades,
 * and trading day boundary detection.
 *
 * Returns "intermediate trades" — raw entry/exit arrays with metadata.
 * Use buildJournalTrade() to convert to final journal format.
 *
 * @param {Array} executions - Raw execution objects from NinjaTrader
 * @returns {Array<IntermediateTrade>} - Array of intermediate trade objects
 */
export const groupExecutionsIntoTrades = (executions) => {
  if (!executions || executions.length === 0) return [];

  const trades = [];

  // Group by Account + Symbol to prevent cross-account position mixing
  const accountGroups = {};

  executions.forEach(exec => {
    const symbol = getSymbol(exec);
    const accountName = exec.AccountName || 'unknown';
    const key = `${accountName}|${symbol}`;

    if (!accountGroups[key]) {
      accountGroups[key] = [];
    }
    accountGroups[key].push(exec);
  });

  // Process each account+symbol group independently
  Object.entries(accountGroups).forEach(([key, groupExecs]) => {
    const [accountName, symbol] = key.split('|');

    // Sort by timestamp, then by ExecutionId for consistent ordering
    groupExecs.sort((a, b) => {
      const timeDiff = new Date(a.Timestamp) - new Date(b.Timestamp);
      if (timeDiff !== 0) return timeDiff;
      // Use ExecutionId sequence number as tiebreaker
      const aSeq = parseInt(a.ExecutionId?.split('|')[0]) || 0;
      const bSeq = parseInt(b.ExecutionId?.split('|')[0]) || 0;
      return aSeq - bSeq;
    });

    // FIFO position tracking
    let position = 0;
    let currentEntries = [];
    let currentExits = [];
    let tradeDirection = null;
    let currentTradingDay = null;

    // Helper: flush current trade and reset state
    const flushTrade = (isOpen, dayBoundaryClosed = false) => {
      if (currentEntries.length > 0) {
        trades.push({
          entries: [...currentEntries],
          exits: [...currentExits],
          accountName,
          symbol,
          direction: tradeDirection,
          isOpen,
          dayBoundaryClosed
        });
      }
      currentEntries = [];
      currentExits = [];
      tradeDirection = null;
    };

    groupExecs.forEach(exec => {
      const qty = parseInt(exec.Quantity) || 0;
      const isBuy = isBuyAction(exec);
      const isSell = isSellAction(exec);
      const tradingDay = getTradingDay(exec.Timestamp);

      // Trading day boundary detection — close position at day change.
      //
      // Special case: the new-day's first execution is an OPPOSITE-direction
      // fill that exactly closes the prior position (e.g. trader held a 5-lot
      // long overnight, exited at next-day open with a 5-lot sell). Without
      // the early-return below, the closing fill would land on the new day
      // with `position === 0` and get classified as a fresh SHORT entry —
      // mis-attributing the exit and leaving the prior trade marked
      // still-open with no exits. Attribute the fill to the prior trade and
      // flush it as CLOSED (with `dayBoundaryClosed=true` so the UI can
      // surface that it crossed a session).
      if (currentTradingDay && tradingDay !== currentTradingDay && position !== 0) {
        const wouldExit = (position > 0 && isSell) || (position < 0 && isBuy);
        const positionChange = isBuy ? qty : -qty;
        const newPosition = position + positionChange;

        if (wouldExit && newPosition === 0) {
          currentExits.push({ ...exec, matchedQuantity: qty });
          flushTrade(false, true);  // CLOSED + dayBoundaryClosed
          position = 0;
          currentTradingDay = tradingDay;
          return;  // exec fully consumed by the prior trade
        }

        // Partial close, close+flip, or same-direction scaling across the
        // boundary — preserve original behavior (flush prior as still-open,
        // process exec fresh in the new day). These are rare; if they bite
        // we revisit with phantom-entry carryover logic.
        flushTrade(true, true);
        position = 0;
      }
      currentTradingDay = tradingDay;

      // Determine entry vs exit based on position state (NOT NinjaTrader flags)
      let isEntry = false;
      let isExit = false;

      if (position === 0) {
        // Flat — any execution opens a new position
        isEntry = true;
        tradeDirection = isBuy ? 'long' : 'short';

        // Warn if a closing action (BuyToCover/SellShort) starts a new position — means we missed the entry
        const action = (exec.Action || '').toLowerCase();
        if (action === 'buy to cover' || action === 'sell short') {
          console.warn(`[TradeProcessor] Orphaned exit detected: "${exec.Action}" on ${symbol} (${accountName}) at position=0. Entry execution was likely never captured.`);
        }
      } else if (position > 0) {
        // Currently long
        isEntry = isBuy;   // Buying more = scale-in
        isExit = !isBuy;   // Selling = exit
      } else {
        // Currently short
        isEntry = isSell;   // Selling more = scale-in
        isExit = !isSell;   // Buying = exit
      }

      // Calculate new position
      const positionChange = isBuy ? qty : -qty;
      const newPosition = position + positionChange;

      if (isEntry) {
        currentEntries.push(exec);
      } else if (isExit) {
        // Add matchedQuantity for FIFO P&L calculation
        currentExits.push({ ...exec, matchedQuantity: Math.min(Math.abs(position), qty) });
      }

      // Check position state transitions
      if (position !== 0 && newPosition === 0) {
        // Position closed — trade complete
        flushTrade(false);
      } else if (position !== 0 && newPosition !== 0 && Math.sign(position) !== Math.sign(newPosition)) {
        // Position flipped (e.g., long -> short)
        // Complete current trade, start new one in opposite direction
        flushTrade(false);
        currentEntries = [exec];
        tradeDirection = newPosition > 0 ? 'long' : 'short';
      }

      position = newPosition;
    });

    // Handle any remaining open position
    if (currentEntries.length > 0 && position !== 0) {
      flushTrade(true);
    }
  });

  return trades;
};

// ─────────────────────────────────────────────
// Build Final Journal Trade from Intermediate
// ─────────────────────────────────────────────

/**
 * Build a final journal trade object from an intermediate trade.
 *
 * This is the single source of truth for trade object shape.
 * Designed to be called once for a complete trade, or repeatedly for
 * an open trade as exits/trims arrive (real-time foundation).
 *
 * @param {IntermediateTrade} intermediateTrade - From groupExecutionsIntoTrades()
 * @param {Object} options
 * @param {Array}  options.existingAccounts - User's journal accounts for matching
 * @param {Object} options.accountMappings - Explicit NT account -> journal account ID map
 * @param {string} options.source - 'auto-sync' | 'import' (affects notes text and metadata)
 * @returns {Object|null} - Final journal trade object, or null if no entries
 */
export const buildJournalTrade = (intermediateTrade, options = {}) => {
  const { entries, exits, accountName, symbol, direction, isOpen, dayBoundaryClosed } = intermediateTrade;
  const { existingAccounts = [], accountMappings = {}, source = 'auto-sync' } = options;

  if (!entries || entries.length === 0) return null;

  // ── Calculate weighted average entry price ──
  let totalEntryQty = 0;
  let weightedPriceSum = 0;
  let totalEntryCommission = 0;

  entries.forEach(exec => {
    const qty = parseInt(exec.Quantity) || 0;
    const price = parseFloat(exec.Price) || 0;
    totalEntryQty += qty;
    weightedPriceSum += price * qty;
    totalEntryCommission += parseFloat(exec.Commission) || 0;
  });

  // Look up instrument spec early for precision helpers.
  // getSpec() normalizes the symbol internally — handles "MNQ 06-26" /
  // "MNQM6" / "MNQ" all the same. If `symbol` is already root-stripped
  // by `getSymbol()` above this is a no-op; if a future code path
  // preserves contract months, the lookup still works.
  const spec = getSpec(symbol) || { tickSize: 0.25, tickValue: 0.50 };
  if (!getSpec(symbol)) {
    console.warn(`[TradeProcessor] Unknown symbol "${symbol}" — using fallback spec. Raw: InstrumentType="${entries[0]?.InstrumentType}", InstrumentName="${entries[0]?.InstrumentName}", Account="${accountName}"`);
  }
  const rawAvgEntry = totalEntryQty > 0 ? weightedPriceSum / totalEntryQty : 0;
  // Snap weighted-average entry to nearest valid tick to prevent floating-point drift
  const avgEntryPrice = snapToTick(rawAvgEntry, spec.tickSize);
  const firstEntry = entries[0];

  // ── ATM template info ──
  const atmTemplateName = firstEntry.AtmTemplateName || firstEntry.AtmTemplate || '';
  const atmInfo = parseAtmTemplateName(atmTemplateName);

  // ── Times (always stored in ET — see CLAUDE.md trade.entryTime contract) ──
  const entryTimeStr = formatEtTime(firstEntry.Timestamp);
  let exitTimeStr = '';
  if (exits.length > 0) {
    exitTimeStr = formatEtTime(exits[exits.length - 1].Timestamp);
  }

  // ── Trading day (6pm ET boundary) ──
  const tradeDate = getTradingDay(firstEntry.Timestamp);

  // ── Format exits ──
  const formattedExits = exits.map(exit => ({
    quantity: parseInt(exit.matchedQuantity) || parseInt(exit.Quantity) || 0,
    price: parseFloat(exit.Price) || 0,
    time: formatEtTime(exit.Timestamp)
  }));

  // Placeholder exit for open trades
  if (formattedExits.length === 0) {
    formattedExits.push({ quantity: '', price: '', time: '' });
  }

  // ── Average exit price (weighted by quantity) ──
  const totalExitQty = formattedExits.reduce((sum, e) => sum + (parseInt(e.quantity) || 0), 0);
  const avgExitPrice = totalExitQty > 0
    ? formattedExits.reduce((sum, e) => sum + (parseFloat(e.price) || 0) * (parseInt(e.quantity) || 0), 0) / totalExitQty
    : '';

  // ── P&L from exits ──
  let totalPnL = 0;
  let totalExitCommission = 0;

  exits.forEach(exit => {
    const qty = parseInt(exit.matchedQuantity) || parseInt(exit.Quantity) || 0;
    const exitPrice = parseFloat(exit.Price) || 0;
    const priceDiff = direction === 'long'
      ? exitPrice - avgEntryPrice
      : avgEntryPrice - exitPrice;
    const ticks = safeTicks(priceDiff, spec.tickSize);
    totalPnL += ticks * spec.tickValue * qty;
    totalExitCommission += parseFloat(exit.Commission) || 0;
  });

  const totalCommission = totalEntryCommission + totalExitCommission;

  // ── Account matching ──
  // Priority: 1) Explicit mapping, 2) Normalized exact match on id/name, 3) Fuzzy includes (only if unambiguous)
  const normalize = (s) => (s || '').replace(/[-_\s]/g, '').toLowerCase();
  let accountId = '';
  if (accountMappings && accountMappings[accountName]) {
    accountId = accountMappings[accountName];
  } else if (existingAccounts?.length > 0) {
    const normNT = normalize(accountName);
    // Try exact normalized match first (handles dash/space/underscore differences)
    let match = existingAccounts.find(a =>
      normalize(a.id) === normNT || normalize(a.name) === normNT
    );
    // Fallback: fuzzy substring match (case-insensitive, both directions).
    // On multi-match, prefer the strictly-longest journal name as the most
    // specific intent — fixes cases like NT "APEX-FT" against journal
    // accounts ["APEX", "APEX-FT-Eval"] where both substring-match but the
    // longer one is the right answer. True ties leave the trade unattributed
    // (the user-facing match-warning system surfaces unmatched accounts).
    if (!match) {
      const ntLower = accountName.toLowerCase();
      const fuzzyMatches = existingAccounts.filter(a => {
        const aName = (a.name || '').toLowerCase();
        const aId = (a.id || '').toLowerCase();
        if (!aName && !aId) return false;
        return (aName && (ntLower.includes(aName) || aName.includes(ntLower)))
            || (aId   && (ntLower.includes(aId)   || aId.includes(ntLower)));
      });

      if (fuzzyMatches.length === 1) {
        match = fuzzyMatches[0];
      } else if (fuzzyMatches.length > 1) {
        const score = (a) => Math.max((a.name || '').length, (a.id || '').length);
        fuzzyMatches.sort((a, b) => score(b) - score(a));
        // Strictly longer than #2 — true ties stay ambiguous on purpose.
        if (score(fuzzyMatches[0]) > score(fuzzyMatches[1])) {
          match = fuzzyMatches[0];
        }
      }
    }
    if (match) accountId = match.id;
  }

  // ── Absolute stop loss price from ATM stop loss points ──
  let calculatedStopLoss = '';
  if (atmInfo.stopLossPoints && avgEntryPrice) {
    const slPrice = direction === 'long'
      ? avgEntryPrice - atmInfo.stopLossPoints
      : avgEntryPrice + atmInfo.stopLossPoints;
    calculatedStopLoss = snapToTick(slPrice, spec.tickSize);
  }

  // ── R-Multiple (only for closed trades with stop loss info) ──
  let calculatedRMultiple = '';
  const tradeIsOpen = isOpen || false;
  if (!tradeIsOpen && atmInfo.stopLossPoints && totalEntryQty > 0) {
    const riskDollars = (atmInfo.stopLossPoints / spec.tickSize) * spec.tickValue * totalEntryQty;
    if (riskDollars > 0 && !isNaN(totalPnL)) {
      calculatedRMultiple = (totalPnL / riskDollars).toFixed(2);
    }
  }

  // ── Source-specific metadata ──
  const isAutoSync = source === 'auto-sync';
  const notesPrefix = isAutoSync ? 'Auto-synced from NinjaTrader' : 'Imported from JournifiSync';
  const allExecutionIds = [...entries, ...exits].map(e => e.ExecutionId);

  // Contract month — sourced from the original execution's InstrumentName.
  // All entries on a single trade share the same contract, so any entry will
  // do; we pick the first. Stored alongside `symbol` (which is rooted) so
  // analytics that want per-contract breakouts (e.g., "did your MNQ 03-26
  // edge transfer to MNQ 06-26?") can opt in. Empty when InstrumentName
  // didn't include a month code (manual sources, root-only inputs).
  const contractMonth = getContractMonth(entries[0] || {});

  // ── Build final trade object ──
  return {
    id: `js_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    date: tradeDate,
    symbol,
    contractMonth,  // e.g., 'M6' for MNQ June 2026; '' if root-only source
    setup: '',
    atmStrategy: atmTemplateName,
    direction,
    entry: avgEntryPrice,
    exit: avgExitPrice,
    entryTime: entryTimeStr,
    exitTime: exitTimeStr,
    exits: formattedExits,
    stopLoss: calculatedStopLoss,
    stopLossPoints: atmInfo.stopLossPoints ? atmInfo.stopLossPoints.toString() : '',
    useStopLossPoints: true,
    quantity: totalEntryQty,
    // Per-entry fills (Wave B.3, v7.4.4). Persisted so behavioral analyzers
    // can detect averaging-down (adding to a losing position) — Hougaard's
    // cardinal violation. Mirror of `exits` shape on the same trade.
    // Empty array for manual entries; populated for sync'd multi-entry trades.
    entries: entries.map(e => ({
      time: e.Time || '',
      price: parseFloat(e.Price) || 0,
      quantity: parseInt(e.Quantity) || 0,
    })),
    pnl: isNaN(totalPnL) ? 0 : parseFloat(totalPnL.toFixed(2)),
    rMultiple: calculatedRMultiple,
    tags: [],
    mistakes: [],
    confluences: [],
    counterConfluences: [],
    notes: `${notesPrefix}\nATM: ${atmTemplateName || 'None'}${!isAutoSync ? `\nCommission: $${totalCommission.toFixed(2)}` : ''}`,
    accountId,
    importedCommission: totalCommission,
    isOpen: isOpen || false,
    dayBoundaryClosed: dayBoundaryClosed || false,
    journaled: false,
    _journifiSync: {
      importedAt: new Date().toISOString(),
      executionIds: allExecutionIds,
      connectionName: firstEntry.ConnectionName,
      ntAccountName: accountName,
      autoSynced: isAutoSync
    }
  };
};

// ─────────────────────────────────────────────
// Convenience: Full Pipeline
// ─────────────────────────────────────────────

/**
 * Process raw executions into final journal trades (full pipeline).
 * Combines groupExecutionsIntoTrades + buildJournalTrade.
 *
 * @param {Array} executions - Raw execution objects from NinjaTrader
 * @param {Object} options
 * @param {Array}  options.existingAccounts - User's journal accounts
 * @param {Object} options.accountMappings - NT account -> journal account ID map
 * @param {string} options.source - 'auto-sync' | 'import'
 * @returns {Array} - Final journal trade objects
 */
export const processExecutionsToTrades = (executions, options = {}) => {
  const intermediateTrades = groupExecutionsIntoTrades(executions);
  return intermediateTrades
    .map(trade => buildJournalTrade(trade, options))
    .filter(Boolean);
};

// ─────────────────────────────────────────────
// Dedup & Repair Primitives (shared across all import paths)
// ─────────────────────────────────────────────

/**
 * Build a canonical signature for a trade. Account-aware so copy-trade
 * scenarios (same order firing across multiple accounts) don't false-match.
 *
 * Shared by real-time hook, manual import, and CSV import to guarantee
 * cross-method consistency.
 *
 * @param {Object} trade - Journal trade object (must have date, symbol, direction, entry, entryTime, quantity, accountId)
 * @returns {string}
 */
export const buildTradeSignature = (trade) => {
  if (!trade) return '';
  const entry = typeof trade.entry === 'number'
    ? trade.entry.toFixed(2)
    : (trade.entry != null ? parseFloat(trade.entry).toFixed(2) : '');
  return [
    trade.date || '',
    trade.symbol || '',
    trade.direction || '',
    entry,
    trade.entryTime || '',
    trade.quantity ?? '',
    trade.accountId || ''
  ].join('|');
};

/**
 * Index existing journal trades by their `_journifiSync.executionIds`.
 * Each exec ID maps to its source trade metadata so we can distinguish:
 *   - True duplicates (same exec ID + same accountId)
 *   - Repair candidates (same exec ID but different/empty accountId — ghost trade)
 *   - Extensions (partial match — new fills arrived for same position)
 *
 * @param {Array} existingTrades - Array of journal trades
 * @returns {Map<string, {tradeId: string, accountId: string, isOpen: boolean, date: string}>}
 */
export const buildExecutionIdIndex = (existingTrades = []) => {
  const index = new Map();
  existingTrades.forEach(trade => {
    const ids = trade?._journifiSync?.executionIds;
    if (!Array.isArray(ids)) return;
    ids.forEach(id => {
      if (!id || index.has(id)) return;
      index.set(id, {
        tradeId: trade.id,
        accountId: trade.accountId || '',
        isOpen: !!trade.isOpen,
        date: trade.date || ''
      });
    });
  });
  return index;
};

/**
 * Classify a newly-built journal trade against the existing journal.
 *
 * Return value is one of:
 *   - { kind: 'new' } — no overlap, create a new trade
 *   - { kind: 'duplicate', matchedTradeId } — fully matches an existing trade with the same accountId
 *   - { kind: 'repair', matchedTradeId, currentAccountId, proposedAccountId } — fully matches an existing trade but its accountId is empty or points elsewhere
 *   - { kind: 'extension', matchedTradeId, newExecIds } — only some exec IDs overlap; remaining are new fills to merge
 *   - { kind: 'skip', reason } — cannot be classified (e.g. no exec IDs)
 *
 * @param {Object} builtTrade - Trade object produced by buildJournalTrade()
 * @param {Map}    execIdIndex - Result of buildExecutionIdIndex()
 * @returns {{kind: string, [key: string]: any}}
 */
export const classifyBuiltTrade = (builtTrade, execIdIndex) => {
  if (!builtTrade) return { kind: 'skip', reason: 'null-trade' };
  const execIds = builtTrade?._journifiSync?.executionIds || [];
  if (execIds.length === 0) return { kind: 'skip', reason: 'no-exec-ids' };

  // Find matches grouped by existing trade id
  const matchesByTradeId = new Map(); // tradeId -> { matchedIds: Set, accountId, isOpen }
  const unmatchedIds = [];
  execIds.forEach(id => {
    const hit = execIdIndex?.get?.(id);
    if (hit) {
      const bucket = matchesByTradeId.get(hit.tradeId) || {
        matchedIds: new Set(),
        accountId: hit.accountId,
        isOpen: hit.isOpen
      };
      bucket.matchedIds.add(id);
      matchesByTradeId.set(hit.tradeId, bucket);
    } else {
      unmatchedIds.push(id);
    }
  });

  // No overlap at all → brand-new trade
  if (matchesByTradeId.size === 0) {
    return { kind: 'new' };
  }

  // If matches span multiple journal trades, that's a structural issue —
  // route it to 'extension' so caller can decide (merge is unsafe here).
  if (matchesByTradeId.size > 1) {
    const tradeIds = Array.from(matchesByTradeId.keys());
    return {
      kind: 'extension',
      matchedTradeId: tradeIds[0],
      newExecIds: unmatchedIds,
      ambiguous: true,
      candidateTradeIds: tradeIds
    };
  }

  const [matchedTradeId, meta] = matchesByTradeId.entries().next().value;
  const intendedAccountId = builtTrade.accountId || '';

  // Partial overlap → extension (new fills to merge into open position)
  if (unmatchedIds.length > 0) {
    return {
      kind: 'extension',
      matchedTradeId,
      newExecIds: unmatchedIds
    };
  }

  // Full overlap — duplicate vs repair decision hinges on account match
  if (meta.accountId && intendedAccountId && meta.accountId === intendedAccountId) {
    return { kind: 'duplicate', matchedTradeId };
  }

  // accountId is missing or mismatched on the stored trade — repair candidate
  return {
    kind: 'repair',
    matchedTradeId,
    currentAccountId: meta.accountId,
    proposedAccountId: intendedAccountId
  };
};
